import React from "react";
import { cn } from "../../lib/utils";
import type { Bucket } from "../../lib/types";

const bucketStyles: Record<string, string> = {
  valid: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  expiring: "bg-amber-50 text-amber-800 ring-amber-200",
  critical: "bg-orange-50 text-orange-800 ring-orange-200",
  expired: "bg-rose-50 text-rose-800 ring-rose-200",
  action: "bg-rose-50 text-rose-700 ring-rose-300",
  unknown: "bg-slate-100 text-slate-600 ring-slate-200",
  none: "bg-slate-100 text-slate-500 ring-slate-200",
};
const bucketLabels: Record<string, string> = {
  valid: "Valid",
  expiring: "Expiring <30d",
  critical: "Critical <7d",
  expired: "Expired",
  action: "Action Required",
  unknown: "No Date",
  none: "No Records",
};

export function Pill({ children, className, testId }: { children: React.ReactNode; className?: string; testId?: string }) {
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset", className)}>
      {children}
    </span>
  );
}

export function BucketBadge({ bucket, label, testId, overdue }: { bucket: Bucket | string; label?: string; testId?: string; overdue?: boolean }) {
  if (overdue) return <Pill testId={testId} className="bg-rose-600 text-white ring-rose-700"><span className="h-1.5 w-1.5 rounded-full bg-white" />{label || "Overdue"}</Pill>;
  return (
    <Pill testId={testId} className={bucketStyles[bucket] || bucketStyles.unknown}>
      {(bucket === "expiring" || bucket === "critical") && <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--gold)]" />}
      {label || bucketLabels[bucket] || bucket}
    </Pill>
  );
}

const statusStyles: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  maintenance: "bg-sky-50 text-sky-800 ring-sky-200",
  grounded: "bg-slate-200 text-slate-700 ring-slate-300",
  archived: "bg-slate-100 text-slate-500 ring-slate-200 line-through",
  open: "bg-rose-50 text-rose-800 ring-rose-200",
  acknowledged: "bg-amber-50 text-amber-800 ring-amber-200",
  resolved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  warning: "bg-amber-50 text-amber-800 ring-amber-200",
  critical: "bg-orange-50 text-orange-800 ring-orange-200",
  expired: "bg-rose-50 text-rose-800 ring-rose-200",
  action: "bg-rose-50 text-rose-700 ring-rose-300",
  inactive: "bg-slate-100 text-slate-500 ring-slate-200",
  paid: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  unpaid: "bg-rose-50 text-rose-700 ring-rose-300",
};

export function StatusBadge({ status, testId }: { status: string; testId?: string }) {
  return (
    <Pill testId={testId} className={cn("capitalize", statusStyles[status] || statusStyles.inactive)}>
      {status}
    </Pill>
  );
}

export function Plate({ plate, className }: { plate?: string | null; className?: string }) {
  return <span className={cn("plate", className)}>{plate || "—"}</span>;
}
